import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import md5 from 'md5';
import SceneGridData from './SceneGridData';
import useTracking from '../Tracking/useTracking';
import Masonry from "react-masonry-css";
import SceneImage from './SceneImage';
import SceneVideo from './SceneVideo';
import DraftModeInfo from './DraftModeInfo';
import DraftModeCode from './DraftModeCode';
import AnalyticsModeInfo from './AnalyticsModeInfo';

import "../../style/SceneGrid.css";

const defaultBreakpoints = {
  default: 4,
  1100: 3,
  760: 2,
  500: 1
};

const getMaxWidth = (breakpoints) => {
  let bp = Object.assign({}, breakpoints);
  bp['1920'] = bp['default'];
  delete (bp['default']);
  let vWidth = window.innerWidth || document.documentElement.clientWidth;
  if (vWidth > 1920) vWidth = 1920;
  let closestSize = 0;
  let smallestSize = 1920;
  for (let k in bp) {
    let size = parseInt(k, 10);
    if (size < smallestSize) smallestSize = size;
    if (size <= vWidth && size > closestSize) {
      closestSize = size;
    }
  }
  if (!closestSize) closestSize = smallestSize;
  return Math.round(vWidth / bp[closestSize] / 40) * 40;
};

const getItemId = (i) => i.banner_id || i.clip_id || md5(i.videoSrc || i.imageSrc);

const SceneGrid = memo(({dataOverride=null}) => {

  //const { lpdata: landingPageData, draftMode, sceneGridData } = useContext(StoreContext);
  const { lpdata: landingPageData, draftMode } = useSelector(state => state.config);
  const { sceneGridData } = useSelector(state => state.sceneGrid);
  const { cp: productGroup } = useSelector(state => state.tracking.trackCookie) || {};

  const { trackAndRedirect } = useTracking();

  //console.log("PG", productGroup);
  if (!landingPageData) return null;
  let breakpointCols = landingPageData.properties.body.masonryBreakpoints || defaultBreakpoints;
  let { imagesRatio = "16:9" } = landingPageData.properties.body;
  let maxImageWidth = getMaxWidth(breakpointCols);

  const handleClick = (data, position) => e => {
    e.stopPropagation();
    e.preventDefault();
    let payload = { component: 'SceneGrid', position: position };
    if (data.clip_id) payload.clip_id = data.clip_id;
    if (data.banner_id) payload.banner_id = getItemId(data);
    if (data.mode) payload.mode = data.mode;

    trackAndRedirect(payload, data.href, data.target, data.clip_id);
  };

  const SceneBlock = ({ item, position, onClick }) => {
    if (productGroup && item.dynamicPG && item.dynamicPG[productGroup]) {
      item = { ...item, ...item.dynamicPG[productGroup] };
    }
    if (item.mode === 'video' || item.videoSrc) return <SceneVideo data={item} position={position} maxWidth={maxImageWidth} onClick={onClick} />;
    return <SceneImage data={item} position={position} maxWidth={maxImageWidth} imagesRatio={imagesRatio} onClick={onClick} />;
  };

  const renderItems = (items) => {
    return items.map((v, position) => (
      <div style={{ position: 'relative' }} key={position}>
        <SceneBlock item={v} position={position} onClick={handleClick(v, position)} />
        <AnalyticsModeInfo item_id={getItemId(v)} />
        {draftMode && <DraftModeInfo id={getItemId(v)} position={position} data={v} />}
      </div>
    ));
  };
  
  if(dataOverride)  return (
    <Masonry
      breakpointCols={breakpointCols}
      className="my-masonry-grid"
      columnClassName="my-masonry-grid_column"
      style={{ maxWidth: "1980px", margin: "auto" }}
    >
      {dataOverride && renderItems(dataOverride)}
    </Masonry>
  );

  return (
    <SceneGridData>
      <Masonry
        breakpointCols={breakpointCols}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
        style={{ maxWidth: "1980px", margin: "auto" }}
      >
        {sceneGridData && renderItems(dataOverride||sceneGridData)}
      </Masonry>
      {draftMode && <DraftModeCode />}
    </SceneGridData>
  );
});

export default SceneGrid;
